<script setup lang="ts" generic="T">
import Multiselect from "vue-multiselect";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { debounce } from "perfect-debounce";
import { DebounceEnum } from "~/utils/enums/debounce.enums";
import { isNoResponseError } from "~/utils/helpers/httpErrors";

const isLoading = ref(false);
const searchQuery = ref("");
const tagSelectRef = ref(null);
const multiselectRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const offset = ref(0); // to track the offset for lazy loading

const PAGE_SIZE = 10;
const isListOpen = ref(false);
// False once a page came back short: the server has nothing after it.
const hasMore = ref(true);
// Concurrency guards, as in SimpleDropdown: `pending` keeps a next page from being asked for while a
// request is in flight (`offset` only advances once it lands, so both would ask for the same page), and
// `requestSeq` makes a superseded reply a no-op.
const pending = ref(0);
let requestSeq = 0;
// From a keystroke until that search's first page has landed, debounce wait included. Until then the
// list and `offset` still belong to the previous search, so no next page may be asked for.
const searchResultsPending = ref(false);

const emit = defineEmits(["update:modelValue", "clickAdd", "searchValue", "open"]);

const props = defineProps({
  options: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  store: {
    type: Object,
    default: null,
  },
  storeObject: {
    type: String,
    default: null,
  },
  modelValue: {
    type: null,
  },
  trackBy: {
    type: String,
    default: "name",
    required: true,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
  add: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "name",
  },
  showNoOptions: {
    type: Boolean,
    default: false,
  },
  showNoResults: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "Choisir une option",
  },
  error: {
    type: Boolean,
    default: false,
  },
  openFetch: {
    type: Boolean,
    default: false,
  },
  nb_Element: {
    type: Number,
    default: 5,
  },
  servicesAPI: {
    type: Function,
  },
});

onMounted(() => {
  if (props.servicesAPI) {
    // Initial page only. The scroll listener is attached in open() — the multiselect content
    // wrapper doesn't exist in the DOM until the dropdown opens (so it's null here). A list opened
    // before this page landed is measured once it has.
    asyncFind("", true).then(loadMoreWhileAtEnd);
  }
});

interface QueryParams {
  [key: string]: any;
}
function updateValue(newValue: any) {
  emit("update:modelValue", newValue);
}
/** Loads the page at `offset`. Resolves to false when nothing was applied: superseded, skipped or unanswered. */
async function asyncFind(query: string, init?: boolean): Promise<boolean> {
  getSearchQuery(query);
  // Imperative, per-interaction fetch (initial open, search, infinite-scroll pagination).
  // Do NOT wrap in useAsyncData: since Nuxt 4 it caches by key and replays the cached result
  // without re-running the handler, which froze the options list (the static "list" key was
  // shared across every instance). servicesAPI (SDK) is fresh on every call.
  const seq = ++requestSeq;
  pending.value += 1;
  if (!init) isLoading.value = true;
  const queryParams: QueryParams = {
    limit: PAGE_SIZE,
    offset: offset.value,
    ...{ search: query },
  };

  let api: any;
  try {
    api = await props.servicesAPI?.(queryParams);
  } catch (error) {
    // No answer came back: nothing to fix in the app, and the next scroll or search asks again.
    if (isNoResponseError(error)) return false;
    throw error;
  } finally {
    pending.value -= 1;
    if (!init) isLoading.value = false;
  }

  // A newer request started while this one was in flight, so its result is the current truth and
  // applying this one would overwrite it. Drop the reply and leave `offset` alone — the newer call owns
  // pagination now.
  if (seq !== requestSeq) return false;

  // A failed or skipped call says nothing about the end of the list: a `servicesAPI` resolves to nothing
  // when it skips (e.g. readonly) or swallows an error. Leave `offset` and `hasMore` as they are, so the
  // next scroll asks for this page again.
  if (!api || api.error?.value) return false;

  const data = api.data?.value?.data?.length ? api.data.value.data : api.data;
  if (data?.length) {
    props.store[props.storeObject] = init ? [] : props.store[props.storeObject] || [];
    props.store[props.storeObject].push(...data);
  }
  hasMore.value = (data?.length ?? 0) >= PAGE_SIZE;
  offset.value += PAGE_SIZE;
  return true;
}

function getSearchQuery(query: string) {
  searchQuery.value = query;
  emit("searchValue", query);
}
const debouncedAsyncFind = debounce(async (q: string) => {
  offset.value = 0;
  const applied = await asyncFind(q, true).finally(() => {
    if (q === searchQuery.value) searchResultsPending.value = false;
  });
  if (!applied) return;

  await nextTick();
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = 0; // Reset scroll position
  }
  await loadMoreWhileAtEnd();
}, DebounceEnum.HIGH);

function searchChange(q: string) {
  const hasAsyncFetch = !!props.servicesAPI;

  // Si pas de fetch async et pas de props.add, on ne fait rien
  if (!hasAsyncFetch && !props.add) {
    return null;
  }

  // Update search query immediately for UI feedback
  getSearchQuery(q);

  // Debounce the API call only if we have async fetch
  if (hasAsyncFetch) {
    searchResultsPending.value = true;
    debouncedAsyncFind(q);
  }
}
function clickAdd() {
  emit("clickAdd");
  // @ts-ignore
  tagSelectRef.value.deactivate();
}

/** The `trackBy` keys of the options the list is drawn from — the caller's, not the store's. */
function optionKeys(): Set<unknown> {
  return new Set((props.options ?? []).map((option) => (option as Record<string, unknown>)[props.trackBy]));
}

/** Whether the list shows its last row — as does a list too short to scroll. */
function isScrolledToEnd(list: HTMLElement): boolean {
  return list.scrollTop + list.clientHeight >= list.scrollHeight - 10;
}

/**
 * Loads the next pages while the open list shows its end. Scrolling to the bottom is how a list
 * normally gets there, but a list too short to scroll never fires `scroll` — and with hide-selected a
 * full page can add few rows or none, its options being already picked. The later pages were then out
 * of reach: « Aucun résultat » on a search whose next page held options still to pick. Stops once the
 * list can scroll, once the server has nothing more, or at a page that brings the list nothing new.
 */
async function loadMoreWhileAtEnd(): Promise<void> {
  await nextTick();
  const list = scrollContainer.value;
  if (!list?.isConnected || !isListOpen.value || !hasMore.value) return;
  if (pending.value > 0 || searchResultsPending.value || !isScrolledToEnd(list)) return;

  const listed = optionKeys();
  if (!(await asyncFind(searchQuery.value))) return;
  await nextTick();
  // A page can reach the store yet not this list: an endpoint ignoring `offset` repeats itself, and some
  // callers show another list than the store they page into (a role-specific one). More pages would
  // then never make the list scroll, only walk the whole endpoint.
  if ([...optionKeys()].some((key) => !listed.has(key))) await loadMoreWhileAtEnd();
}

function open() {
  isListOpen.value = true;
  nextTick(() => {
    // Only wire lazy-loading when this dropdown actually fetches. Static-options usages have
    // nothing to page.
    if (!props.servicesAPI) return;
    // vue-multiselect only mounts .multiselect__content-wrapper while the dropdown is open, so it
    // isn't in the DOM at onMounted. (Re)acquire it and attach the infinite-scroll listener on each
    // open — otherwise the listener never fires and lazy-loading past the first page never happens.
    const wrapper = multiselectRef.value?.querySelector(".multiselect__content-wrapper") as HTMLElement | null;
    if (wrapper && wrapper !== scrollContainer.value) {
      scrollContainer.value?.removeEventListener("scroll", loadMoreWhileAtEnd);
      scrollContainer.value = wrapper;
      wrapper.addEventListener("scroll", loadMoreWhileAtEnd);
    }
    // A list too short to scroll can't be scrolled to its end: its next pages are asked for here.
    loadMoreWhileAtEnd();
  });
  if (props.openFetch) {
    emit("open");
  }
}
</script>
<template>
  <div ref="multiselectRef" class="tags" :aria-busy="searchResultsPending || pending > 0">
    <multiselect
      ref="tagSelectRef"
      :model-value="props.modelValue"
      :options="props.options || []"
      :loading="isLoading"
      :track-by="trackBy"
      :label="label"
      :placeholder="placeholder"
      :max-height="40 * props.nb_Element"
      :multiple="true"
      :taggable="false"
      :show-labels="false"
      :show-pointer="false"
      open-direction="bottom"
      :show-no-results="showNoResults"
      :disabled="disabled"
      :show-no-options="showNoOptions"
      :hide-selected="true"
      :class="{ error: props.error }"
      @search-change="searchChange"
      @update:modelValue="updateValue"
      @open="open"
      @close="isListOpen = false"
    >
      <template #tag="{ option, remove }">
        <div class="multiselect__tag">
          <div class="name flex flex-row gap-2">
            <div class="truncate">
              <slot name="tag" :option="option"></slot>
            </div>
          </div>
          <img
            src="/icons/generic/close.svg"
            alt=""
            class="dropdown__img__close"
            @mousedown.prevent.stop="remove(option)"
          />
        </div>
      </template>
      <template v-if="props.options?.length || searchQuery.length" #caret="{ toggle, isOpen }">
        <span class="multiselect__select">
          <SvgIcon class="w-full h-full" name="arrow_down" @mousedown.prevent.stop="toggle"></SvgIcon>
        </span>
      </template>
      <template v-if="props.add && searchQuery.length" #afterList>
        <div class="dropdown__padding inline-flex gap-[8px] items-center text-primary-blue-color" @click="clickAdd">
          <SvgIcon name="plus"></SvgIcon>
          <span>
            <slot name="add">Ajouter "{{ searchQuery }}"</slot>
          </span>
        </div>
      </template>
      <template #option="{ option }">
        <slot name="option" :option="option"></slot>
      </template>
      <template #noResult>
        <span>Aucun résultat</span>
      </template>
    </multiselect>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/dropdowns/multiselect";
</style>
